import React, { useState, useEffect } from "react";
import { ArrowRightIcon, ShoppingCartIcon } from "lucide-react";
import { Link } from "~/components/Link";
import { useSidebar } from "~/components/SidebarLayout/SidebarContext";
import { Routes } from "~/routes";
import { useWindowSize } from "~/utils/useWindowSize";
import { usePageScrollLock } from "~/hooks/use-page-scroll-lock";
import { useScrollThreshold } from "~/hooks/use-scroll-threshold";
import * as Analytics from "~/modules/dashboard_page_unlocks/analytics";
import { Button } from "~/components/ui/button";
import { CheckoutForm } from "~/modules/billing/components/CheckoutForm";
import { UPGRADE_TO_UNLOCK_DRAWER_BENEFITS } from "~/modules/dashboard_page_unlocks/consts";
import {
    DrawerBackdrop,
    DrawerContent,
    DrawerFooter,
    DrawerFooterItem,
    DrawerHeader,
    DrawerSheet,
    DrawerSubheader,
} from "~/components/Drawer";

// // // //

/**
 * UpgradeToUnlockDrawer
 * Bottom drawer shown on dashboard pages when a logged-in user has no page
 * unlocks remaining in the current window. Prompts them to upgrade to
 * RWA.xyz Pro. Mirrors the `UnlockPageDrawer` / `PaywallDrawer` look and
 * behavior (appears after scrolling 30% of the page).
 *
 * NOTE - only render this for logged-in users; it performs no auth checks.
 */
export function UpgradeToUnlockDrawer(props: { organizationId: string }) {
    const { organizationId } = props;
    const [isVisible, setIsVisible] = useState(false);
    const { sidebarWidth } = useSidebar();

    const { isLg, isXl, is2xl } = useWindowSize();
    const isLargeScreen = isLg || isXl || is2xl;

    // Show the drawer after scrolling 30% down the page
    useScrollThreshold({
        thresholdPercent: 30,
        onThresholdCrossed: () => setIsVisible(true),
    });

    // Lock page scroll while the drawer is visible
    usePageScrollLock({ lockScroll: isVisible });

    // Track once when the gate is rendered - independent of the scroll threshold
    // NOTE - mount-only on purpose, so later prop changes don't re-fire the event
    useEffect(() => {
        Analytics.trackUpgradeToUnlockDrawerGateViewed({ organizationId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isVisible) {
            Analytics.trackUpgradeToUnlockDrawerShown({ organizationId });
        }
    }, [isVisible, organizationId]);

    if (!isVisible) return null;

    return (
        <UpgradeToUnlockDrawerView
            organizationId={organizationId}
            isLargeScreen={isLargeScreen}
            sidebarWidth={sidebarWidth}
        />
    );
}

// // // //

export function UpgradeToUnlockDrawerView(props: {
    organizationId: string;
    isLargeScreen: boolean;
    sidebarWidth: number;
}) {
    const { organizationId, isLargeScreen, sidebarWidth } = props;

    return (
        <>
            <DrawerBackdrop />

            {/* Drawer - positioned to avoid sidebar on large screens */}
            <DrawerSheet offsetLeft={isLargeScreen ? sidebarWidth : 0}>
                <DrawerContent>
                    {/* Headline */}
                    <DrawerHeader>
                        Upgrade to RWA.xyz Pro to unlock this page
                    </DrawerHeader>

                    {/* Subheading */}
                    <DrawerSubheader>
                        You&apos;ve used all of your free page unlocks for now.
                        Upgrade to access all metrics, asset reference data, and
                        advanced analytics across every tokenized RWA.
                    </DrawerSubheader>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                        <CheckoutForm
                            text="Upgrade to Pro"
                            organizationId={organizationId}
                            enableAdditionalSeatsSelect={false}
                            variant="primaryLight"
                            size="lg"
                            className="rounded-full text-base"
                            icon={<ShoppingCartIcon />}
                        />
                        <Button
                            asChild
                            variant="outline"
                            size="lg"
                            className="rounded-full text-base"
                        >
                            <Link
                                href={Routes.salesPage}
                                analyticsLabel={
                                    Analytics.DashboardPageUnlocksUserEvents
                                        .UpgradeToUnlockDrawerViewPricingLinkClick
                                }
                            >
                                View pricing
                                <ArrowRightIcon />
                            </Link>
                        </Button>
                    </div>

                    {/* Benefits */}
                    <DrawerFooter>
                        {UPGRADE_TO_UNLOCK_DRAWER_BENEFITS.map((benefit) => (
                            <DrawerFooterItem key={benefit} showCheckmark>
                                {benefit}
                            </DrawerFooterItem>
                        ))}
                    </DrawerFooter>
                </DrawerContent>
            </DrawerSheet>
        </>
    );
}
